import React, { useEffect, useState } from 'react';
import { Card, Grid, CardContent, Typography, Button } from '@mui/material';
import { purpleText, internalCard, normalText, changeAddressBtn } from '../../styles';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { addressSchema } from '../../validations';
import api from '~/services/api';
import { storeState } from '~/store';
import useDebounce from '~/utils/useDebounce';
import CustomizedInputs from '~/components/Input';

interface Props {
  address:
    | {
        address_id: number;
        street: string;
        additional_details: string;
        zipcode: string;
        neighborhood: string;
        city: string;
        state: string;
        country: string;
        number: string;
      }
    | undefined;
  updateAddress: (data: AddressInputs) => Promise<void>;
}

export type AddressInputs = {
  street: string;
  number: string;
  additional_details: string;
  neighborhood: string;
  city: string;
  state: string;
  zipcode: string;
};

function formatCep(raw: string) {
  const digits = raw.replace(/\D/g, '');
  return digits.length === 8 ? digits.replace(/^(\d{5})(\d{3})$/, '$1-$2') : raw;
}

export function AddressCard({ address, updateAddress }: Props) {
  if (!address) return null;

  const [allowChangeAddress, setAllowChangeAddress] = useState(false);
  const [changedAddress, setChangedAddress] = useState<AddressInputs>({} as AddressInputs);
  const [loading, setLoading] = useState(false);

  const {
    control,
    setValue,
    handleSubmit,
    getValues,
    watch,
    formState: { errors: _errors }
  } = useForm<AddressInputs>({
    defaultValues: {
      ...address,
      additional_details: ''
    },
    resolver: yupResolver(addressSchema)
  });

  const zipcodeState = watch('zipcode');
  const debouncedZipCode = useDebounce(zipcodeState, 1000);

  const handleZipCodeSearch = async () => {
    const zipcode = getValues('zipcode');
    const cleanZipcode = zipcode.replace(/[^\d]+/g, '');

    if (cleanZipcode.length === 8) {
      try {
        const {
          data: { data }
        } = await api.get(`ZipCodes?zipcode=${cleanZipcode}`);

        Object.entries(data).forEach(([key, value]) => {
          setValue(key as keyof AddressInputs, value as string);
        });
      } catch {}
    }
  };

  const onSubmit: SubmitHandler<AddressInputs> = async (data) => {
    setLoading(true);
    try {
      await updateAddress(data);
      setChangedAddress(data);
      setAllowChangeAddress(false);
    } catch {
      Object.entries(address).forEach(([key, value]) => {
        setValue(key as keyof AddressInputs, value as string);
      });
      storeState.addToast({
        type: 'error',
        title: 'Aconteceu um erro',
        message: 'Não foi possível atualizar o endereço. Tente novamente mais tarde'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedZipCode !== address.zipcode) {
      handleZipCodeSearch();
    }
  }, [debouncedZipCode, address.zipcode]);

  return (
    <Card sx={{ margin: { xs: '2rem 0.5rem', sm: '2rem 1rem', md: '2rem 2rem' } }}>
      <CardContent sx={internalCard}>
        <Typography variant="h6" align="left" sx={{ paddingBottom: 1 }}>
          3 - Endereço completo (pernoite)
        </Typography>

        {!allowChangeAddress ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  CEP:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {formatCep(changedAddress.zipcode ?? address.zipcode)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Endereço:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {changedAddress.street ?? address.street}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Número:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {changedAddress.number ?? address.number}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Complemento:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {(changedAddress.additional_details ?? address.additional_details) || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Bairro:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {changedAddress.neighborhood ?? address.neighborhood}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Cidade:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {changedAddress.city ?? address.city}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={12}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Estado:
                </Typography>
                <Typography variant="subtitle2" sx={normalText}>
                  {changedAddress.state ?? address.state}
                </Typography>
              </Grid>
            </Grid>
            <Button type="button" sx={changeAddressBtn} onClick={() => setAllowChangeAddress(true)}>
              Alterar
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  CEP:
                </Typography>
                <Controller
                  name="zipcode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      mask="zipcode"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Endereço:
                </Typography>
                <Controller
                  name="street"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Número:
                </Typography>
                <Controller
                  name="number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Complemento:
                </Typography>
                <Controller
                  name="additional_details"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Bairro:
                </Typography>
                <Controller
                  name="neighborhood"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Cidade:
                </Typography>
                <Controller
                  name="city"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={12}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Estado:
                </Typography>
                <Controller
                  name="state"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Button type="submit" sx={changeAddressBtn} disabled={loading}>
              {loading ? 'Confirmando...' : 'Confirmar'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
